import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BarlowBurstResultsModel } from '../models/barlow-burst-results.model';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { DesignLimitsResultsModel } from '../models/design-limits-results.model';
import { SafetyFactorResultModel } from '../models/safety-factor-results.model';
import { ConnectionSafetyFactorResultsModel } from '../models/connection-safety-factor-results.model';
import { HangerLiftoffForceResult } from '../models/hangar-lift-off-force-result.model';
import { DisplacementResultsModel } from '../models/displacement-results.model';
import { AxialLoadResultsModel } from '../models/axial-load-results.model';
import { CollapseResultsModel } from '../models/collapse-results.model';
import { PackerLoadResultsModel } from '../models/packer-load-results.model';
import { MaxAllowableWearResultsModel } from '../models/max-allowable-wear.model';
import { HollidayStressResultsModel } from '../models/holliday-stress-ratio.model';

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

@Injectable({
  providedIn: 'root'
})
export class StressResultsService {

  constructor(private http: HttpClient) { }
  private packerLoadsCache$: Observable<PackerLoadResultsModel[]> | null = null;
  private displacementResultsCache$: Observable<DisplacementResultsModel[]> | null = null;
  private barlowBurstCache$: Observable<BarlowBurstResultsModel[]> | null = null;
  private collapseResultsCache$: Observable<CollapseResultsModel[]> | null = null;
  private hollidayStressRatioCache$: Observable<any[]> | null = null;
  private axialResultsCache$: Observable<AxialLoadResultsModel[]> | null = null;
  private safetyFactorResultsCache$: Observable<SafetyFactorResultModel[]> | null = null;
  private connectionSafetyFactorCache$: Observable<ConnectionSafetyFactorResultsModel[]> | null = null;
  private designLimitsPlotsCache$: Observable<DesignLimitsResultsModel> | null = null;
  private liftoffResultsCache$: Observable<HangerLiftoffForceResult[]> | null = null;

  public getDisplacementResults(): Observable<DisplacementResultsModel[]> {
    if (!this.displacementResultsCache$) {
      this.displacementResultsCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/displacements`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.displacementResultsCache$ = null, 1000);
    return this.displacementResultsCache$;
  }

  public getBarlowBurstResults(): Observable<BarlowBurstResultsModel[]> {
    if (!this.barlowBurstCache$) {
      this.barlowBurstCache$ = this.http
        .get<BarlowBurstResultsModel[]>(`${PeriforApiEndpoint}/stressResults/burst`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.barlowBurstCache$ = null, 1000);
    return this.barlowBurstCache$;
  }

  public getCollapseResults(): Observable<CollapseResultsModel[]> {
    if (!this.collapseResultsCache$) {
      this.collapseResultsCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/collapse`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.collapseResultsCache$ = null, 1000);
    return this.collapseResultsCache$;
  }

  public getAxialResults(): Observable<AxialLoadResultsModel[]> {
    if (!this.axialResultsCache$) {
      this.axialResultsCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/axial`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.axialResultsCache$ = null, 1000);
    return this.axialResultsCache$;
  }

  public getHollidayStressRatioResults(): Observable<HollidayStressResultsModel[]> {
    if (!this.hollidayStressRatioCache$) {
      this.hollidayStressRatioCache$ = this.http
        .get<HollidayStressResultsModel[]>(`${PeriforApiEndpoint}/stressResults/hollidayStressRatio`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.hollidayStressRatioCache$ = null, 1000);
    return this.hollidayStressRatioCache$;
  }

  public getSafetyFactorResults(): Observable<SafetyFactorResultModel[]> {
    if (!this.safetyFactorResultsCache$) {
      this.safetyFactorResultsCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/safetyFactors`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.safetyFactorResultsCache$ = null, 1000);
    return this.safetyFactorResultsCache$;
  }

  public getConnectionSafetyFactorResults(): Observable<ConnectionSafetyFactorResultsModel[]> {
    if (!this.connectionSafetyFactorCache$) {
      this.connectionSafetyFactorCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/connectionSafetyFactors`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.connectionSafetyFactorCache$ = null, 1000);
    return this.connectionSafetyFactorCache$;
  }

  public getDesignLimitsPlots(): Observable<DesignLimitsResultsModel> {
    if (!this.designLimitsPlotsCache$) {
      this.designLimitsPlotsCache$ = this.http
        .get<any>(`${PeriforApiEndpoint}/stressResults/designLimitsPlot`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.designLimitsPlotsCache$ = null, 1000);
    return this.designLimitsPlotsCache$;
  }

  public getLiftoffResults(): Observable<HangerLiftoffForceResult[]> {
    if (!this.liftoffResultsCache$) {
      this.liftoffResultsCache$ = this.http
        .get<any[]>(`${PeriforApiEndpoint}/stressResults/liftoff`)
        .pipe(catchError(() => of([])), shareReplay(1));
    }
    setTimeout(() => this.liftoffResultsCache$ = null, 1000);
    return this.liftoffResultsCache$;
  }

  public getPackerLoads(): Observable<PackerLoadResultsModel[]> {
    if (!this.packerLoadsCache$) {
      this.packerLoadsCache$ = this.http
        .get<any>(`${PeriforApiEndpoint}/stressResults/packerLoads`)
        .pipe(catchError(() => of(null)), shareReplay(1)
        );
    }
    setTimeout(() => this.packerLoadsCache$ = null, 1000);
    return this.packerLoadsCache$;
  }

  public getCasingWearResults(): Observable<MaxAllowableWearResultsModel> {
    return this.http
      .get<any>(`${PeriforApiEndpoint}/stressResults/maxAllowableWear`)
      .pipe(catchError(() => of(null)), shareReplay(1));
  }
}
